/**
 * Conditional bundle discounts — the ONE rule for deciding whether a combination
 * of selected bundle options earns a price reduction, and by how much.
 *
 * A bundle prices as `base_price + Σ price_delta − computeBundleDiscount(...)`.
 * The Σ part is unconditional and per-option; this module is the only place a
 * *combination* of options changes the price.
 *
 * A rule applies iff EVERY condition holds. A condition names one slot and the
 * any-of set of options that satisfy it. So `[{Wing Set: [upgrade×3]}, {Boardbag:
 * [Add Boardbag]}]` reads "an upgraded wing set AND a boardbag" — AND across
 * slots, OR within a slot. That mirrors the DB shape (one condition row per
 * required slot, `option_ids` as the any-of set).
 *
 * Pure + dependency-free by design: the same verdict has to hold on the
 * storefront PDP, the dealer picker, the Stripe charge in `checkout.ts`, and the
 * order reconstruction in `stripe-webhook.ts`. A display/charge mismatch here is
 * a revenue bug, so all four read this function rather than re-deriving it.
 *
 * NOTE: inlined into `apps/portal/api/_lib/bundleDiscounts.ts` for the Vercel
 * serverless bundle (workspace packages don't resolve there). Keep in sync —
 * drift is guarded by `apps/portal/tests/unit/bundleDiscountsInline.test.ts`.
 */

/** One required slot for a rule; satisfied when the slot's selected option is in `option_ids`. */
export type BundleDiscountCondition = {
  slot_id: string
  option_ids: string[]
}

export type BundleDiscountRule = {
  id: string
  label: string
  /** Whole dollars, stored POSITIVE; subtracted from the bundle total. */
  discount_amount: number
  conditions: BundleDiscountCondition[]
}

/** slot_id → option_id chosen for that slot (null = nothing chosen). */
export type BundleSelectionEntry = {
  slot_id: string
  option_id: string | null
}

/**
 * True when every condition of `rule` is satisfied by `selection`.
 *
 * A rule with no conditions returns FALSE, never true. Vacuous truth would make
 * an empty/malformed rule apply to every configuration — free money on every
 * order. The safe default for "we can't tell what this rule requires" is to not
 * discount.
 */
function bundleDiscountRuleMatches(
  selection: BundleSelectionEntry[],
  rule: BundleDiscountRule,
): boolean {
  if (rule.conditions.length === 0) return false
  return rule.conditions.every(cond => {
    if (cond.option_ids.length === 0) return false
    const chosen = selection.find(s => s.slot_id === cond.slot_id)?.option_id
    return !!chosen && cond.option_ids.includes(chosen)
  })
}

/**
 * Total discount (a POSITIVE number to subtract) plus the rules that earned it.
 *
 * Multiple matching rules stack additively. Inactive rules are the caller's
 * concern to filter — the DB query does it via `is_active`.
 */
export function computeBundleDiscount(
  selection: BundleSelectionEntry[],
  rules: BundleDiscountRule[],
): { total: number; applied: BundleDiscountRule[] } {
  const applied = rules.filter(rule => bundleDiscountRuleMatches(selection, rule))
  const total = applied.reduce((acc, rule) => acc + rule.discount_amount, 0)
  return { total, applied }
}
